import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Plus, Edit, Power, X } from 'lucide-react';
import { packageApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';

const defaultFormData = {
  name: '',
  duration_days: 30,
  min_amount: 50,
  max_amount: 100000,
  daily_roi: 0.6,
  commission_levels: [true, false, false, false, false],
  promotion_requirements: {
    direct_referrals: 0,
    level2: 0,
    level3: 0,
    level4: 0,
    level5: 0
  },
  commission_rates: {
    direct: 8,
    level2: 0,
    level3: 0,
    level4: 0,
    level5: 0
  },
  business_volume_required: 0,
  tier_order: 1
};

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await packageApi.getAll();
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        duration_days: pkg.duration_days,
        min_amount: pkg.min_amount,
        max_amount: pkg.max_amount,
        daily_roi: pkg.daily_roi,
        commission_levels: pkg.commission_levels,
        promotion_requirements: pkg.promotion_requirements || defaultFormData.promotion_requirements,
        commission_rates: pkg.commission_rates || defaultFormData.commission_rates,
        business_volume_required: pkg.business_volume_required || 0,
        tier_order: pkg.tier_order || 1
      });
    } else {
      setEditingPackage(null);
      setFormData(defaultFormData);
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Please enter a package name');
      return;
    }

    setSubmitting(true);
    try {
      if (editingPackage) {
        await packageApi.update(editingPackage.id, formData);
        toast.success('Package updated');
      } else {
        await packageApi.create(formData);
        toast.success('Package created');
      }
      setShowModal(false);
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (pkgId) => {
    try {
      await packageApi.toggle(pkgId);
      toast.success('Package status updated');
      fetchPackages();
    } catch (error) {
      toast.error('Failed to update package');
    }
  };

  const updateCommissionLevel = (index, value) => {
    const newLevels = [...formData.commission_levels];
    newLevels[index] = value;
    setFormData({ ...formData, commission_levels: newLevels });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-packages">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-amber-500/10">
            <Package className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl text-white font-medium">Package Management</h1>
            <p className="text-sm text-zinc-500">Create and manage staking packages</p>
          </div>
        </div>

        <Button onClick={() => handleOpenModal()} className="gold-btn" data-testid="create-package-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Package
        </Button>
      </div>

      {/* Packages Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg, idx) => (
          <motion.div
            key={pkg.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className={`glass-card p-6 ${!pkg.is_active ? 'opacity-60' : ''}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-white font-medium text-lg">{pkg.name}</h3>
                <p className="text-xs text-zinc-500">{pkg.duration_days} days</p>
              </div>
              <span className={`badge ${pkg.is_active ? 'badge-success' : 'badge-danger'}`}>
                {pkg.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Tier Order</span>
                <span className="text-purple-400 font-bold">#{pkg.tier_order || 1}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Range</span>
                <span className="text-white">${pkg.min_amount} - ${pkg.max_amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Daily ROI</span>
                <span className="text-green-400">{pkg.daily_roi}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Biz Vol Required</span>
                <span className="text-amber-400">${(pkg.business_volume_required || 0).toLocaleString()}</span>
              </div>
            </div>

            <div className="border-t border-white/10 pt-4 mb-4">
              <p className="text-xs text-zinc-500 mb-2">Commission Levels</p>
              <div className="flex gap-1">
                {pkg.commission_levels?.map((enabled, i) => (
                  <div 
                    key={i}
                    className={`px-2 py-1 rounded text-[10px] ${
                      enabled ? 'bg-green-500/20 text-green-400' : 'bg-zinc-800 text-zinc-600'
                    }`}
                  >
                    L{i + 1}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleOpenModal(pkg)}
                className="flex-1 border-white/10"
              >
                <Edit className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleToggle(pkg.id)}
                className={`flex-1 ${pkg.is_active ? 'border-red-500/30 text-red-400' : 'border-green-500/30 text-green-400'}`}
              >
                <Power className="w-3 h-3 mr-1" /> {pkg.is_active ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingPackage ? 'Edit Package' : 'Create Participation Package'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Package Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Gold Tier"
                  className="bg-black/50 border-white/10"
                  data-testid="package-name"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Duration (Days)</label>
                <Input
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({...formData, duration_days: parseInt(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Min Participation ($)</label>
                <Input
                  type="number"
                  value={formData.min_amount}
                  onChange={(e) => setFormData({...formData, min_amount: parseFloat(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Max Participation ($)</label>
                <Input
                  type="number"
                  value={formData.max_amount}
                  onChange={(e) => setFormData({...formData, max_amount: parseFloat(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Daily ROI (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.daily_roi}
                  onChange={(e) => setFormData({...formData, daily_roi: parseFloat(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Tier Order (1=Bronze, 2=Silver...)</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.tier_order}
                  onChange={(e) => setFormData({...formData, tier_order: parseInt(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
            </div>

            {/* Business Volume Required */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <label className="text-xs text-amber-400 mb-2 block font-medium">Business Volume Required for This Tier ($)</label>
              <p className="text-[10px] text-zinc-500 mb-2">Total team investment amount required to qualify for this tier (0 for Bronze)</p>
              <Input
                type="number"
                value={formData.business_volume_required}
                onChange={(e) => setFormData({...formData, business_volume_required: parseFloat(e.target.value) || 0})}
                className="bg-black/50 border-white/10"
                placeholder="e.g., 5000"
              />
            </div>

            {/* Commission Levels */}
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Commission Levels Enabled</label>
              <div className="flex gap-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <label key={i} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.commission_levels[i]}
                      onCheckedChange={(checked) => updateCommissionLevel(i, checked)}
                    />
                    <span className="text-sm text-zinc-300">Level {i + 1}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Promotion Requirements */}
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Promotion Requirements</label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-zinc-500">Direct Referrals</label>
                  <Input
                    type="number"
                    value={formData.promotion_requirements.direct_referrals}
                    onChange={(e) => setFormData({
                      ...formData, 
                      promotion_requirements: {...formData.promotion_requirements, direct_referrals: parseInt(e.target.value)}
                    })}
                    className="bg-black/50 border-white/10 h-8 text-sm"
                  />
                </div>
                {[2, 3, 4, 5].map((level) => (
                  <div key={level}>
                    <label className="text-[10px] text-zinc-500">Level {level} Refs</label>
                    <Input
                      type="number"
                      value={formData.promotion_requirements[`level${level}`]}
                      onChange={(e) => setFormData({
                        ...formData, 
                        promotion_requirements: {...formData.promotion_requirements, [`level${level}`]: parseInt(e.target.value)}
                      })}
                      className="bg-black/50 border-white/10 h-8 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Commission Rates */}
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Commission & Benefit Share Rates (%)</label>
              <p className="text-[10px] text-zinc-600 mb-2">Level 1 = Direct commission on deposits. Levels 2-5 = Benefit share from daily ROI</p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-zinc-500">Direct Commission</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.commission_rates.direct}
                    onChange={(e) => setFormData({
                      ...formData, 
                      commission_rates: {...formData.commission_rates, direct: parseFloat(e.target.value)}
                    })}
                    className="bg-black/50 border-white/10 h-8 text-sm"
                  />
                </div>
                {[2, 3, 4, 5].map((level) => (
                  <div key={level}>
                    <label className="text-[10px] text-zinc-500">Benefit Share Lv.{level}</label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.commission_rates[`level${level}`]}
                      onChange={(e) => setFormData({
                        ...formData, 
                        commission_rates: {...formData.commission_rates, [`level${level}`]: parseFloat(e.target.value)}
                      })}
                      className="bg-black/50 border-white/10 h-8 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleSubmit} disabled={submitting} className="flex-1 gold-btn">
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  editingPackage ? 'Update Package' : 'Create Package'
                )}
              </Button>
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-white/10">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
